import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, Compass, Loader2, Search } from "lucide-react";
import { fmtSugestao } from "./descobertaHelpers";

const fmtKm = (v) => `${Number(v).toFixed(1).replace(".", ",")} km`;

function Resumo({ data }) {
  if (!data) return null;
  return (
    <div className="text-xs text-muted-foreground space-y-1" data-testid="sugerir-resumo">
      <p>
        Regra: até <b>{fmtKm(data.raio_km)}</b> do centro de <b>{data.cidade || "—"}</b> = Centro; fora disso, a direção define Norte/Sul/Leste/Oeste.
        {" "}<b>{data.com_sugestao}</b> de <b>{data.total}</b> bairro(s) com sugestão{data.sem_geo ? ` · ${data.sem_geo} sem coordenadas (use “Buscar bairros da cidade” para geolocalizar)` : ""}.
      </p>
      {data.zonas_sem_regiao?.length > 0 && (
        <p className="text-amber-700" data-testid="sugerir-zonas-sem-regiao">Sem região cadastrada para: {data.zonas_sem_regiao.join(", ")} — cadastre regiões com esses nomes para receber sugestão.</p>
      )}
    </div>
  );
}

// Sugestão de Região em massa para bairros já cadastrados (usa as coordenadas conhecidas de cada bairro).
export function SugerirRegioesDialog({ open, onClose, regioes = [], onAplicado }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [incluirComRegiao, setIncluirComRegiao] = useState(false);
  const [busca, setBusca] = useState("");
  const [escolha, setEscolha] = useState({});
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    setLoading(true); setEscolha({});
    api.get("/bairros/sugerir-regioes", { params: { somente_sem_regiao: !incluirComRegiao } })
      .then((r) => { setData(r.data); setSelected(new Set(r.data.itens.filter((i) => i.sugestao?.regiao_id).map((i) => i.bairro_id))); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Erro ao calcular sugestões"))
      .finally(() => setLoading(false));
  }, [open, incluirComRegiao]);

  const itens = useMemo(() => data?.itens || [], [data]);
  const visiveis = useMemo(() => { const q = busca.trim().toLowerCase(); return q ? itens.filter((i) => i.nome.toLowerCase().includes(q)) : itens; }, [itens, busca]);
  const regiaoDe = (i) => escolha[i.bairro_id] || i.sugestao?.regiao_id || "";
  const aplicaveis = itens.filter((i) => selected.has(i.bairro_id) && regiaoDe(i));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const todosVisiveis = visiveis.length > 0 && visiveis.every((i) => selected.has(i.bairro_id));
  const toggleVisiveis = () => setSelected((s) => { const n = new Set(s); visiveis.forEach((i) => (todosVisiveis ? n.delete(i.bairro_id) : n.add(i.bairro_id))); return n; });

  const aplicar = async () => {
    setSaving(true);
    try {
      const r = await api.post("/bairros/sugerir-regioes/aplicar", { itens: aplicaveis.map((i) => ({ bairro_id: i.bairro_id, regiao_id: regiaoDe(i) })) });
      toast.success(`${r.data.updated} bairro(s) com região definida${r.data.skipped ? ` · ${r.data.skipped} ignorado(s)` : ""}`);
      onAplicado?.(r.data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao aplicar regiões"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="sugerir-regioes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Compass className="w-5 h-5 text-orange-600" />Sugerir regiões pela localização</DialogTitle>
          <Resumo data={data} />
        </DialogHeader>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={incluirComRegiao} onChange={(e) => setIncluirComRegiao(e.target.checked)} className="accent-orange-600 w-4 h-4" data-testid="sugerir-incluir-com-regiao" />
            Incluir bairros que já têm região
          </label>
          <div className="relative ml-auto">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar bairro" className="pl-8 h-9 w-56" data-testid="sugerir-busca" />
          </div>
        </div>
        <div className="border rounded-lg overflow-auto flex-1 min-h-0 max-h-[52vh]">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground" data-testid="sugerir-loading"><Loader2 className="w-4 h-4 animate-spin" />Calculando…</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-orange-50/60">
                  <TableHead className="w-10"><input type="checkbox" checked={todosVisiveis} onChange={toggleVisiveis} className="accent-orange-600 w-4 h-4" data-testid="sugerir-chk-visiveis" /></TableHead>
                  <TableHead>Bairro</TableHead>
                  <TableHead>Região atual</TableHead>
                  <TableHead>Posição</TableHead>
                  <TableHead className="w-60">Região sugerida</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visiveis.map((i) => (
                  <TableRow key={i.bairro_id} data-testid={`sugerir-row-${i.bairro_id}`} data-zona={i.sugestao?.zona || ""} className={!i.sugestao?.regiao_id && !escolha[i.bairro_id] ? "opacity-70" : selected.has(i.bairro_id) ? "bg-orange-50/40" : ""}>
                    <TableCell><input type="checkbox" checked={selected.has(i.bairro_id)} onChange={() => toggle(i.bairro_id)} className="accent-orange-600 w-4 h-4" data-testid={`sugerir-chk-${i.bairro_id}`} /></TableCell>
                    <TableCell className="font-medium">{i.nome}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{i.regiao_nome || "—"}</TableCell>
                    <TableCell className="text-xs">{i.sugestao ? fmtSugestao(i.sugestao) : <span className="text-amber-700">{i.motivo}</span>}</TableCell>
                    <TableCell>
                      <Select value={regiaoDe(i)} onValueChange={(v) => setEscolha((m) => ({ ...m, [i.bairro_id]: v }))}>
                        <SelectTrigger className={`h-8 text-xs ${!regiaoDe(i) ? "border-rose-300" : ""}`} data-testid={`sugerir-regiao-${i.bairro_id}`}><SelectValue placeholder={i.motivo || "Escolha…"} /></SelectTrigger>
                        <SelectContent>{regioes.map((r) => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
                {visiveis.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">{itens.length === 0 ? "Todos os bairros já têm região" : "Nenhum bairro nesta busca"}</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </div>
        <div className="flex items-center gap-2 pt-3 border-t">
          <span className="text-sm text-muted-foreground mr-auto" data-testid="sugerir-selecionados"><b>{aplicaveis.length}</b> bairro(s) receberão região</span>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={aplicar} disabled={saving || loading || aplicaveis.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sugerir-aplicar">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}Aplicar em {aplicaveis.length} bairro(s)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
